import {
  CustomDatePicker,
  dayjsWithTimezone,
  disabledDateRule,
  maxDateRule,
  minDateRule,
  twClassMerge, validDateRule 
} from '@healthhub/react-ui';
import type { FormInstance } from 'antd';
import { Form, Typography } from 'antd';
import type { Store } from 'antd/es/form/interface';
import type { ConfigType, Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { Button } from '@shared/components';
import { useLayoutViewContext } from '@shared/context';
import { getSubtractDateDays } from '@shared/utils/date/dateUtil';
import { requiredRule } from '@shared/utils/validator';

const { Text } = Typography;
const { useWatch } = Form;

interface Props {
  className?: string;
  dateRangeDaysLimit: number;
  dateRangeOffset?: number;
  filterBtnText?: string;
  fixedEndDate: ConfigType;
  fixedStartDate: ConfigType;
  form: FormInstance<Store>;
  handleResetFilters: () => void;
  maxDate?: ConfigType;
  minDate?: ConfigType;
}

const FilterComponent = ({
  className,
  dateRangeDaysLimit,
  dateRangeOffset,
  filterBtnText,
  fixedEndDate,
  fixedStartDate,
  form,
  handleResetFilters,
  maxDate,
  minDate,
}: Props) => {
  const { isMobileView, isPortalView, isSharedServiceView, isStandardView } = useLayoutViewContext();

  const { t } = useTranslation();
  const toDate = useWatch('ToDate', form);
  const fromDate = useWatch('FromDate', form);

  const handleValidateToDate = (current: Dayjs) =>
    current < dayjsWithTimezone(fixedStartDate || fromDate).subtract(dateRangeOffset ?? 0, 'day') ||
    current > dayjsWithTimezone(fixedEndDate || undefined).subtract(dateRangeOffset ?? 0, 'day');

  const handleValidateFromDate = (current: Dayjs) => {
    const baseStartDate = fixedStartDate
      ? dayjsWithTimezone(fixedStartDate)
      : (getSubtractDateDays(dateRangeDaysLimit, false) as Dayjs);

    return (
      current < baseStartDate.subtract(dateRangeOffset ?? 0, 'day').subtract(1, 'day') ||
      current > dayjsWithTimezone(fixedEndDate || toDate).subtract(dateRangeOffset ?? 0, 'day')
    );
  };

  return (
    <div
      className={twClassMerge(
        `flex h-full w-full flex-col space-y-6 bg-white ${!isMobileView && !isSharedServiceView && 'p-4'}`,
        className
      )}
    >
      <section>
        <div className="flex flex-col gap-2">
          <Text className="text-base">{filterBtnText || t('filterScreen.fromText')}</Text>
          <Form.Item
            name="FromDate"
            rules={[
              requiredRule({}),
              validDateRule(),
              ...(minDate ? [minDateRule(minDate)] : []),
              ...(maxDate ? [maxDateRule(maxDate)] : []),
              ...(minDate || maxDate ? [] : [disabledDateRule(handleValidateFromDate)]),
            ]}
          >
            {maxDate || minDate ? (
              <CustomDatePicker
                maxDate={maxDate?.toString() ?? undefined}
                minDate={minDate?.toString() ?? undefined}
                placeholder="Start Date"
                required
              />
            ) : (
              <CustomDatePicker disabledDate={handleValidateFromDate} placeholder="Start Date" required />
            )}
          </Form.Item>
        </div>
        <div className="flex flex-col gap-2">
          <Text className="text-base">{t('filterScreen.to')}</Text>
          <Form.Item
            name="ToDate"
            rules={[
              requiredRule({}),
              validDateRule(),
              ...(minDate ? [minDateRule(minDate)] : []),
              ...(maxDate ? [maxDateRule(maxDate)] : []),
              ...(minDate || maxDate ? [] : [disabledDateRule(handleValidateToDate)]),
            ]}
          >
            {maxDate || minDate ? (
              <CustomDatePicker
                maxDate={maxDate?.toString() ?? undefined}
                minDate={minDate?.toString() ?? undefined}
                placeholder="End Date"
                required
              />
            ) : (
              <CustomDatePicker disabledDate={handleValidateToDate} placeholder="End Date" required />
            )}
          </Form.Item>
        </div>
      </section>
      <div
        className={twClassMerge(
          'flex w-full gap-2 justify-center',
          (isMobileView || isSharedServiceView || isStandardView) && 'justify-end flex-col mt-auto h-full',
          isPortalView && 'flex-row-reverse'
        )}
      >
        <Button className="w-full" disabled={!toDate || !fromDate} htmlType="submit">
          {t('filterScreen.apply')}
        </Button>
        <Button className="w-full" onClick={handleResetFilters} variant="secondary">
          {t('filterScreen.reset')}
        </Button>
      </div>
    </div>
  );
};

export default FilterComponent;
